/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef, useState } from 'react';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import {
  CASH_INFLOW_CATEGORIES,
  CASH_MOVEMENT_CATEGORY_LABELS,
  CASH_OUTFLOW_CATEGORIES,
  categoryRequiresNote,
} from '../infra/cashOutflowCategories'
import type {
  CashMovementCategory,
  CashMovementDirection,
} from '../infra/cashOutflowCategories';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { OperationalFilterChipGroup } from '../../shared/filters/OperationalFilterPrimitives';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalShell } from '../../shared/modals/ModalShell'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import { formatMoney } from './workspace/cashFormatMoney';

type Props = {
  visible: boolean;
  submitting: boolean;
  /** 'out' egreso (default) | 'in' ingreso manual (Registrar ingreso). */
  direction?: CashMovementDirection;
  /** Efectivo esperado en caja — hint contextual y aviso si el egreso lo supera. */
  expectedCashInDrawer?: number | null;
  /** Permite alternar Egreso/Ingreso dentro del mismo modal. */
  onDirectionChange?: (direction: CashMovementDirection) => void;
  onClose: () => void;
  onSubmit: (input: { category: CashMovementCategory; amountText: string; note: string }) => void;
};

export function CashOutflowRegisterModal({
  visible,
  submitting,
  direction = 'out',
  expectedCashInDrawer,
  onDirectionChange,
  onClose,
  onSubmit,
}: Props) {
  const isInflow = direction === 'in';
  const categories: readonly CashMovementCategory[] = isInflow ? CASH_INFLOW_CATEGORIES : CASH_OUTFLOW_CATEGORIES;
  const defaultCategory: CashMovementCategory = isInflow ? 'owner_contribution' : 'supplies';
  const [category, setCategory] = useState<CashMovementCategory>(defaultCategory);
  const [amountText, setAmountText] = useState('');
  const [note, setNote] = useState('');

  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);
  const amountInputRef = useRef<TextInput>(null);
  const noteInputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (!visible) return;
    setCategory(defaultCategory);
    setAmountText('');
    setNote('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, direction]);

  const { confirm: confirmDiscard, ConfirmModal: DiscardConfirmModal } = useOperationalConfirm();
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const isDirty = amountText.trim() !== '' || note.trim() !== '';

  const handleClose = () => {
    if (submitting) return;
    // Si el confirm ya está arriba, back/backdrop lo cierra a él.
    if (discardConfirmOpen) return;
    // BUG-06: con monto/nota cargados, confirmar antes de perderlos.
    if (!isDirty) {
      onClose();
      return;
    }
    setDiscardConfirmOpen(true);
    void confirmDiscard({
      title: isInflow ? 'Descartar ingreso' : 'Descartar egreso',
      message: 'Hay datos cargados sin guardar. Si cerrás, se pierden.',
      confirmLabel: 'Descartar',
      destructive: true,
      iconName: 'trash-outline',
      presentation: 'overlay',
    }).then((confirmed) => {
      setDiscardConfirmOpen(false);
      if (confirmed) onClose();
    });
  };

  const handleSubmit = () => {
    onSubmit({ category, amountText, note });
  };

  // BUG-03: deshabilitar submit cuando faltan campos obligatorios
  const needsNote = categoryRequiresNote(direction, category);
  const canSubmit =
    !submitting &&
    amountText.trim() !== '' &&
    (!needsNote || note.trim() !== '');

  const parsedAmount = parseDecimalInput(amountText, { min: 0.01, money: true });
  const overExpected =
    !isInflow &&
    expectedCashInDrawer != null &&
    parsedAmount != null &&
    parsedAmount > expectedCashInDrawer;

  const title = isInflow ? 'Registrar ingreso' : 'Registrar egreso';
  const subtitle = isInflow
    ? 'Sumá plata a la caja (aporte, fondo, otro ingreso).'
    : 'Sacá plata de la caja. Si es una factura, registrala en Gastos.';
  const submitLabel = isInflow ? 'Registrar ingreso' : 'Registrar egreso';

  const directionToggle = onDirectionChange ? (
    <View testID="caja.movement.direction-toggle">
      <OperationalFilterChipGroup
        options={[
          { value: 'out', label: 'Egreso' },
          { value: 'in', label: 'Ingreso' },
        ]}
        value={direction}
        onChange={(d) => onDirectionChange(d as CashMovementDirection)}
        disabled={submitting}
      />
    </View>
  ) : null;

  return (
    <ModalShell
      visible={visible}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      scrollable={false}
      scrollRef={scrollRef}
      contentRef={scrollContentRef}
      scrollBottomInset={spacing.sm}
      scrollContentStyle={modalExperience.formPanel}
      overlay={DiscardConfirmModal}
      footer={
        <View style={posUi.modalActionsStack}>
          <TouchableOpacity
            style={[posUi.primaryButton, posUi.modalActionFullWidth, !canSubmit && posUi.disabledCard]}
            onPress={handleSubmit}
            disabled={!canSubmit}
            accessibilityRole="button"
          >
            <Text style={posUi.primaryButtonText}>{submitting ? 'Guardando…' : submitLabel}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[posUi.secondaryButton, posUi.modalActionFullWidth]}
            onPress={handleClose}
            disabled={submitting}
            accessibilityRole="button"
          >
            <Text style={posUi.secondaryButtonText}>Cancelar</Text>
          </TouchableOpacity>
        </View>
      }
    >
      {directionToggle}

      {expectedCashInDrawer != null ? (
        <Text style={posUi.caption} testID="caja.movement.expected-hint">
          Debería haber {formatMoney(expectedCashInDrawer)} en caja.
        </Text>
      ) : null}

      <ModalFormField label="Categoría" required>
        <OperationalFilterChipGroup
          options={categories.map((key) => ({
            value: key,
            label: CASH_MOVEMENT_CATEGORY_LABELS[key],
          }))}
          value={category}
          onChange={setCategory}
          disabled={submitting}
        />
      </ModalFormField>

      <ModalFormField label="Monto" required>
        <NumericTextInput
          ref={amountInputRef}
          kind="decimalMoney"
          value={amountText}
          onChangeText={setAmountText}
          placeholder="Ej. 1500"
          placeholderTextColor={ticketColors.ink3}
          style={modalExperience.formInput}
          editable={!submitting}
          accessibilityLabel={isInflow ? 'Monto del ingreso' : 'Monto del egreso'}
        />
        {overExpected ? (
          <Text style={[posUi.caption, { color: ticketColors.warn }]} testID="caja.movement.over-expected">
            Supera el efectivo esperado en caja ({formatMoney(expectedCashInDrawer!)}).
          </Text>
        ) : null}
      </ModalFormField>

      <ModalFormField label={needsNote ? 'Nota (obligatoria)' : 'Nota (opcional)'}>
        <TextInput
          ref={noteInputRef}
          value={note}
          onChangeText={setNote}
          placeholder={needsNote ? (isInflow ? 'Describí el ingreso' : 'Describí el egreso') : 'Detalle opcional'}
          placeholderTextColor={ticketColors.ink3}
          style={[modalExperience.formInput, modalExperience.formInputMultiline]}
          multiline
          editable={!submitting}
        />
      </ModalFormField>
    </ModalShell>
  );
}
